import { Spacing } from '@persona-id/design-tokens';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useRouteMatch } from 'react-router';
import { matchPath, Navigate, Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import NavBack from 'dashboard/components/NavBack';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Environment } from 'dashboard/models/environment';
import { Configuration } from 'dashboard/models/grid-layout';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import { TransactionType, TransactionTypeAttributes } from 'dashboard/models/transaction-type';
import {
  TransactionModule,
  TransactionTypeGridLayout,
} from 'dashboard/models/transaction-type-grid-layout';
import { Trigger } from 'dashboard/models/trigger';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import DashboardTransactionTypesActiveView from 'dashboard/views/DashboardTransactionTypesActiveFieldsView';
import DashboardTransactionInactiveFieldsView from 'dashboard/views/DashboardTransactionTypesInactiveFieldsView';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { JsonSchemaObject } from 'lib/json-schema';

import { TransactionLayoutEditor } from './TransactionLayoutEditor';

export const TOP_BUTTON_CONTAINER_PORTAL_ID = 'transaction-type-top-button-container-portal';
interface DashboardTransactionTypesShowViewProps {
  deactivateTransactionType: (transactionTypeId: string) => Promise<void>;
  activateTransactionType: (transactionTypeId: string) => Promise<void>;
  deactivateTransactionTypeField: (
    transactionType: TransactionType,
    fieldSchemaKey: string,
  ) => Promise<void>;
  activateTransactionTypeField: (
    transactionType: TransactionType,
    fieldSchemaKey: string,
  ) => Promise<void>;
  transactionType: TransactionType;
  organization: Organization;
  currentEnvironment: Environment;
  dataSchema: JsonSchemaObject;
  isUpdatingTransactionType: boolean;
  updateTransactionType: (
    transactionType: TransactionType,
    deactivateFieldSchemaKeys: string[],
  ) => Promise<void>;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
  fieldKeyToWorkflowWithRelationships: Record<
    string,
    { workflow: Workflow; workflowVersion: WorkflowVersion; trigger: Trigger }
  >;
  onUpdateTransactionType: (
    attributes: TransactionTypeAttributes,
    deactivateFieldSchemaKeys: string[],
  ) => Promise<void>;
  onUpdateTransactionTypeGridLayout: (gridLayouts: TransactionTypeGridLayout) => Promise<void>;
  onCreateTransactionTypeGridLayout: (
    configuration: Configuration<TransactionModule>,
  ) => Promise<void>;
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
  gridLayouts: TransactionTypeGridLayout[];
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

const MainColumn = styled.div`
  flex: 1 0 710px;
  overflow: auto;
`;

const SettingsContainer = styled.div`
  padding: ${Spacing[7]};
`;

export const RIGHT_COLUMN_PORTAL_ID = 'transaction-type-portal';

const DashboardTransactionTypesShowView = ({
  deactivateTransactionType,
  activateTransactionType,
  deactivateTransactionTypeField,
  activateTransactionTypeField,
  transactionType,
  updateTransactionType,
  showMessage,
  fieldKeyToWorkflowWithRelationships,
  organization,
  currentEnvironment,
  onUpdateTransactionType,
  isUpdatingTransactionType,
  dataSchema,
  enabledInquiryTemplates,
  publishedVerificationTemplates,
  gridLayouts,
  onUpdateTransactionTypeGridLayout,
  onCreateTransactionTypeGridLayout,
}: DashboardTransactionTypesShowViewProps) => {
  const { name } = transactionType.attributes;

  const [currentName, setCurrentName] = useState(name);

  useEffect(() => {
    setCurrentName(name);
  }, [name]);

  const handleDeactivateField = useCallback(
    (fieldKey: string) => {
      return deactivateTransactionTypeField(transactionType, fieldKey);
    },
    [transactionType, deactivateTransactionTypeField],
  );

  const handleActivateField = useCallback(
    (fieldKey: string) => {
      return activateTransactionTypeField(transactionType, fieldKey);
    },
    [transactionType, activateTransactionTypeField],
  );

  const handleDeactivateTransactionType = useCallback(() => {
    deactivateTransactionType(transactionType.id);
  }, [transactionType, deactivateTransactionType]);

  const handleActivateTransactionType = useCallback(() => {
    activateTransactionType(transactionType.id);
  }, [transactionType, activateTransactionType]);

  const handleUpdateTransactionType = useCallback(
    (updatedTransactionType: TransactionType, deactivateFieldSchemaKeys: string[]) => {
      updateTransactionType(updatedTransactionType, deactivateFieldSchemaKeys);
    },
    [updateTransactionType],
  );

  const activeFieldsView = (
    <DashboardTransactionTypesActiveView
      fieldKeyToWorkflowWithRelationships={fieldKeyToWorkflowWithRelationships}
      handleDeactivateTransactionType={handleDeactivateTransactionType}
      handleActivateTransactionType={handleActivateTransactionType}
      handleDeactivateField={handleDeactivateField}
      handleUpdateTransactionType={handleUpdateTransactionType}
      showMessage={showMessage}
      transactionType={transactionType}
      isUpdatingTransactionType={isUpdatingTransactionType}
    />
  );
  const inactiveFieldsView = (
    <DashboardTransactionInactiveFieldsView
      transactionType={transactionType}
      handleActivateField={handleActivateField}
    />
  );

  const tabs = [
    {
      name: 'Fields',
      path: '/fields',
      contents: activeFieldsView,
    },
    {
      name: 'Inactive Fields',
      path: '/inactive-fields',
      contents: inactiveFieldsView,
    },
  ];
  const INACTIVE_FIELDS_PATH = '/dashboard/transaction-types/:transactionTypeId/inactive-fields';
  const { pathname } = useLocation();

  const isInactiveFieldsRoute = matchPath(
    {
      path: INACTIVE_FIELDS_PATH,
      end: true,
    },
    pathname,
  );

  const handleSaveName = (name: string) => {
    return onUpdateTransactionType(
      {
        ...transactionType.attributes,
        name,
      },
      [],
    );
  };

  const { isExact } = useRouteMatch();
  const isTransactionLayoutEditorEnabled = useFeatureFlag('dashboard:txn-layout-editor');

  let navBackLink = '/dashboard/transaction-types';
  // if we're in a subroute and the transaction layout editor is enabled, nav back to layout editor
  if (!isExact && isTransactionLayoutEditorEnabled) {
    navBackLink = `/dashboard/transaction-types/${transactionType.id}`;
  }

  return (
    <>
      <Helmet title={`${transactionType.attributes.name} - Types - Transactions`} />
      <FeatureFlagged
        featureFlag='dashboard:txn-layout-editor'
        fallback={isInactiveFieldsRoute ? inactiveFieldsView : activeFieldsView}
      >
        <Header
          prepend={<NavBack to={navBackLink} />}
          title={
            <DynamicInput
              value={currentName}
              onChange={(e) => {
                setCurrentName(e.target.value);
              }}
              onBlur={() => {
                if (currentName !== name) {
                  handleSaveName(currentName);
                }
              }}
            />
          }
        >
          <div id={TOP_BUTTON_CONTAINER_PORTAL_ID} />
        </Header>
        <Container>
          <MainColumn>
            <SentryRoutes>
              <Route
                path='/'
                index
                element={
                  <TransactionLayoutEditor
                    transactionType={transactionType}
                    organization={organization}
                    currentEnvironment={currentEnvironment}
                    dataSchema={dataSchema}
                    gridLayouts={gridLayouts}
                    fieldSchema={transactionType.attributes['field-schemas']}
                    onTransactionTypeGridLayoutCreate={onCreateTransactionTypeGridLayout}
                    onTransactionTypeGridLayoutSave={onUpdateTransactionTypeGridLayout}
                    enabledInquiryTemplates={enabledInquiryTemplates}
                    publishedVerificationTemplates={publishedVerificationTemplates}
                  />
                }
              />
              <Route
                path='settings/*'
                element={
                  <SettingsContainer>
                    <RoutedTabbedSection tabs={tabs} />
                  </SettingsContainer>
                }
              />
              <Route path='*' element={<Navigate to='.' replace />} />
            </SentryRoutes>
          </MainColumn>
          {/* children render their panels here so they appear outside of the tab hierarchy */}
          <div id={RIGHT_COLUMN_PORTAL_ID} />
        </Container>
      </FeatureFlagged>
    </>
  );
};

export default DashboardTransactionTypesShowView;
